import mixpanel from 'mixpanel-browser'
import { useEffect, useState } from 'react'

import { Button } from './Button'
import { ConsentCheckbox } from './ConsentCheckbox'
import { Headline } from './Headline'
import { Input } from './Input'

import { IS_DOMAIN_REQUIRED } from '../config'
import { useFormValidation } from '../hooks/UseFormValidation'

interface EmailProps {
  error: string
  host: string
  handleNavigate: (view: string) => void
  handleSubscribe: (email: string) => void
}

export function Email({ error, host, handleSubscribe }: EmailProps) {
  const formValidator = useFormValidation()
  const [consentChecked, setConsentChecked] = useState(false)

  useEffect(() => {
    mixpanel.track('EmailSignUpScreenViewed')
  }, [])

  useEffect(() => {
    if (error) {
      formValidator.setCustomError(error)
    }
  }, [error])

  const isDw = (email: string): boolean => {
    return email.endsWith('@digitalwisdom.ai')
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    let email = formValidator.value?.trim() || ''
    if (isDw(email)) {
      handleSubscribe(email)
      return
    }

    if (!email.includes('@')) {
      email = `${email}@${host}`
      handleSubscribe(email)
      return
    }

    const emailDomain = email.split('@')[1]
    if (IS_DOMAIN_REQUIRED && emailDomain !== host) {
      formValidator.setCustomError('Email domain must match the website host.')
      return
    }
    formValidator.setCustomError('')
    handleSubscribe(email)
  }

  const isFormValid = formValidator.isValid && consentChecked

  return (
    <div className='mx-auto flex-grow flex items-center relative'>
      <div className='text-center relative z-0'>
        <Headline
          title='Your preview is'
          highlight=' being built!'
          paragraph="Enter your email below, and we'll send you a link to check it out."
        />
        <div className='animate-slidein700 opacity-0 -mt-5 flex items-center justify-center gap-x-6'>
          <form
            onSubmit={handleSubmit}
            className='w-full lg:col-span-5 lg:pt-2'
          >
            <div className='flex gap-x-4 flex-col max-w-[370px] mx-auto'>
              <div className='mt-9 p-1 flex flex-col rounded-[12px] '>
                <div className='mt-7 flex rounded-[12px] overflow-hidden outline outline-1 -outline-offset-1 outline-gray-400 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-gray-600'>
                  <Input
                    id='email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    inputMode='email'
                    autoCapitalize='none'
                    placeholder='Your email'
                    value={formValidator.value}
                    onInputChange={formValidator.handleChange}
                    className='outline-none h-[44px] rounded-l-[12px] lowercase'
                  />
                </div>

                {formValidator.error && (
                  <div className='mh-5 mt-2 text-red-500 text-sm text-left'>
                    {formValidator.error}
                  </div>
                )}

                <div className='mt-4'></div>
                <ConsentCheckbox
                  checked={consentChecked}
                  onChange={setConsentChecked}
                />

                <div className='mt-5 flex items-center justify-center gap-x-4 '>
                  <Button
                    disabled={!isFormValid}
                    title='Complete Setup'
                    type='submit'
                    className={`flex-grow bg-dw-black focus-visible:outline-dw-gold text-white ${
                      !isFormValid ? '' : 'hover:opacity-90'
                    }`}
                  />
                </div>
                <div className='text-sm mt-2 text-gray-600'>
                  <p className='mt-2'>We don't spam or sell your email.</p>
                  <p className='text-xs italic text-gray-500 mt-3'>
                    Unless you send us the greatest email ever written, in which
                    case we might sell it but you'll get a cut.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
